import { Box, Divider, Stack, Typography } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { POSITIVE_DECIMAL_REGEX } from "../../../../../constants/patterns"
import {
    TT_DEVICE_PHASES,
    TT_EVDC_MODEL_REF,
    TT_HP_FUNCTION,
    TT_HP_TOT_INPUT_RATED_CURRENT,
    TT_HP_TYPE,
    TT_MODEL,
    TT_VOLTAGE,
    TT_HP_TOT_INPUT_RATED_POWER,
    TT_HP_MAX_DEMAND_CURRENT,
    TT_HP_MAX_DEMAND_POWER,
    TT_HP_PRODUCT_CODE,
    TT_HP_HARMONIC_STANDARDS,
    TT_HP_FLICKER_STANDARDS,
    TT_HP_MODULE,
    TT_HP_MODULE_REF,
    TT_HP_BACK_UP_HEATER_PRESENT,
    TT_HP_BACK_UP_HEATER_PHASES,
    TT_HP_BACK_UP_HEATER_MODULE_REF,
    TT_HP_BACK_UP_HEATER_MODULE,
    TT_HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT,
    TT_HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER,
    TT_HP_BOOST_HEATER_PRESENT,
    TT_HP_BOOST_HEATER_PHASES,
    TT_HP_BOOST_HEATER_MODULE_REF,
    TT_HP_BOOST_HEATER_MODULE,
    TT_HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT,
    TT_HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER,
    TT_HP_WATER_HEATER_PRESENT,
    TT_HP_WATER_HEATER_PHASES,
    TT_HP_WATER_HEATER_MODULE_REF,
    TT_HP_WATER_HEATER_MODULE,
    TT_HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT,
    TT_HP_WATER_HEATER_MODULE_INPUT_RATED_POWER,
    TT_HP_HEAT_PUMP_PRESENT,
    TT_HP_HEAT_PUMP_PHASES,
    TT_HP_HEAT_PUMP_MODULE_REF,
    TT_HP_HEAT_PUMP_MODULE,
    TT_HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT,
    TT_HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER,
    TT_HP_NOM_CURRENT_W35,
    TT_HP_NOM_CURRENT_W45,
    TT_HP_NOM_CURRENT_W55,
    TT_HP_NOM_CURRENT_W65,
} from "../../../../../constants/tooltips"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import ValidatedRadioGroup from "../../../../inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import ValidatedTextField from "../../../../inputs/validated/validatedTextField"
import CoreDeviceDetailsSkeleton from "../../../../loading/skeletons/coreDeviceDetailsSkeleton"
import { useFormGenerator } from "../../../../submitApplication/single/formGenerator/useFormGenerator"
import { AppCard } from "../../../../surfaces/appCard"
import DisplayIf from "../../../../wrappers/displayIf"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    EVDC_VOLTAGE_OPTIONS,
    LCT_PHASES_OPTIONS,
} from "../../formSpecs/evdcSpec"
import {
    HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT_REG,
    HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER_REG,
    HP_BACK_UP_HEATER_MODULE_OPTIONS,
    HP_BACK_UP_HEATER_MODULE_REF_REG,
    HP_BACK_UP_HEATER_MODULE_REG,
    HP_BACK_UP_HEATER_PHASES_REG,
    HP_BACK_UP_HEATER_PRESENT_REG,
    HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT_REG,
    HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER_REG,
    HP_BOOST_HEATER_MODULE_OPTIONS,
    HP_BOOST_HEATER_MODULE_REF_REG,
    HP_BOOST_HEATER_MODULE_REG,
    HP_BOOST_HEATER_PHASES_REG,
    HP_BOOST_HEATER_PRESENT_REG,
    HP_FLICKER_STANDARDS_REG,
    HP_FLICKER_TEST_STANDARDS,
    HP_FUNCTIONS,
    HP_FUNCTION_REG,
    HP_HARMONIC_STANDARDS_REG,
    HP_HARMONIC_TEST_STANDARDS,
    HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT_REG,
    HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER_REG,
    HP_HEAT_PUMP_MODULE_OPTIONS,
    HP_HEAT_PUMP_MODULE_REF_REG,
    HP_HEAT_PUMP_MODULE_REG,
    HP_HEAT_PUMP_PHASES_REG,
    HP_HEAT_PUMP_PRESENT_REG,
    HP_MAX_DEMAND_CURRENT_REG,
    HP_MAX_DEMAND_POWER_REG,
    HP_MODEL_REF_REG,
    HP_MODEL_REG,
    HP_MODULE_OPTIONS,
    HP_MODULE_REF_REG,
    HP_MODULE_REG,
    HP_NOM_CURRENT_W35_REG,
    HP_NOM_CURRENT_W45_REG,
    HP_NOM_CURRENT_W55_REG,
    HP_NOM_CURRENT_W65_REG,
    HP_PHASES_REG,
    HP_PRODUCT_CODE,
    HP_TOT_INPUT_RATED_CURRENT_REG,
    HP_TOT_INPUT_RATED_POWER_REG,
    HP_TYPES,
    HP_TYPE_REG,
    HP_VOLTAGE_REG,
    HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT_REG,
    HP_WATER_HEATER_MODULE_INPUT_RATED_POWER_REG,
    HP_WATER_HEATER_MODULE_OPTIONS,
    HP_WATER_HEATER_MODULE_REF_REG,
    HP_WATER_HEATER_MODULE_REG,
    HP_WATER_HEATER_PHASES_REG,
    HP_WATER_HEATER_PRESENT_REG,
    IS_MODULE_PRESENT,
    IS_MODULE_PRESENT_OPTIONS,
    getHpUpdatedFormData,
    hpDeviceFormLabelMapping,
    mapHpRegisterNameToValues,
} from "../../formSpecs/hpSpec"
import { HpDeviceFormData } from "../../types/providerTypes"

interface ICoreDeviceDetailsProps {
    context: Context<IDeviceFormContextValue<HpDeviceFormData>>
}

/** Displays a form page that allows the user to enter the core
 * information about an HP device
 */
const CoreHpDeviceDetailsForm = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        formData,
        setFormData,
        errors: contextErrors,
        saveDraft,
        saveDraftLoading,
    } = useContext(context)

    const isFirstPage = currentIndex === 0

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useFormGenerator(mapHpRegisterNameToValues(formData))

    const updateCoreDeviceDetails = (): void => {
        const updatedFormData = getHpUpdatedFormData(formData, watch)
        setFormData(updatedFormData)
    }

    const onSaveDraft = (): void => {
        const updatedFormData = getHpUpdatedFormData(formData, watch)
        setFormData(updatedFormData)
        saveDraft(updatedFormData)
    }

    const onClickBack = isFirstPage
        ? undefined
        : (): void => {
              updateCoreDeviceDetails()
              setCurrentIndex(currentIndex - 1)
          }

    const onClickNext = (): void => {
        updateCoreDeviceDetails()
        setCurrentIndex(currentIndex + 1)
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    return (
        <>
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MODEL_REG}
                label={hpDeviceFormLabelMapping[HP_MODEL_REG]}
                placeholder="Enter device model..."
                tooltip={TT_MODEL}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MODEL_REF_REG}
                label={hpDeviceFormLabelMapping[HP_MODEL_REF_REG]}
                placeholder="Enter device model reference..."
                tooltip={TT_EVDC_MODEL_REF}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={HP_FUNCTION_REG}
                label={hpDeviceFormLabelMapping[HP_FUNCTION_REG]}
                options={HP_FUNCTIONS}
                tooltip={TT_HP_FUNCTION}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={HP_TYPE_REG}
                label={hpDeviceFormLabelMapping[HP_TYPE_REG]}
                options={HP_TYPES}
                tooltip={TT_HP_TYPE}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_PHASES_REG}
                label={hpDeviceFormLabelMapping[HP_PHASES_REG]}
                radioOptions={LCT_PHASES_OPTIONS}
                tooltip={TT_DEVICE_PHASES}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={HP_VOLTAGE_REG}
                label={hpDeviceFormLabelMapping[HP_VOLTAGE_REG]}
                options={EVDC_VOLTAGE_OPTIONS}
                tooltip={TT_VOLTAGE}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_TOT_INPUT_RATED_CURRENT_REG}
                label={hpDeviceFormLabelMapping[HP_TOT_INPUT_RATED_CURRENT_REG]}
                placeholder="Enter total input rated current..."
                tooltip={TT_HP_TOT_INPUT_RATED_CURRENT}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Total input rated current must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_TOT_INPUT_RATED_POWER_REG}
                label={hpDeviceFormLabelMapping[HP_TOT_INPUT_RATED_POWER_REG]}
                placeholder="Enter total input rated power..."
                tooltip={TT_HP_TOT_INPUT_RATED_POWER}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Total input rated power must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MAX_DEMAND_CURRENT_REG}
                label={hpDeviceFormLabelMapping[HP_MAX_DEMAND_CURRENT_REG]}
                placeholder="Enter device max current demand..."
                tooltip={TT_HP_MAX_DEMAND_CURRENT}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device max current demand must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MAX_DEMAND_POWER_REG}
                label={hpDeviceFormLabelMapping[HP_MAX_DEMAND_POWER_REG]}
                placeholder="Enter device max demand power..."
                tooltip={TT_HP_MAX_DEMAND_POWER}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device max demand power must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_PRODUCT_CODE}
                label={hpDeviceFormLabelMapping[HP_PRODUCT_CODE]}
                placeholder="Enter device product code..."
                tooltip={TT_HP_PRODUCT_CODE}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_HARMONIC_STANDARDS_REG}
                label={hpDeviceFormLabelMapping[HP_HARMONIC_STANDARDS_REG]}
                radioOptions={HP_HARMONIC_TEST_STANDARDS}
                tooltip={TT_HP_HARMONIC_STANDARDS}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_FLICKER_STANDARDS_REG}
                label={hpDeviceFormLabelMapping[HP_FLICKER_STANDARDS_REG]}
                radioOptions={HP_FLICKER_TEST_STANDARDS}
                tooltip={TT_HP_FLICKER_STANDARDS}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={HP_MODULE_REG}
                label={hpDeviceFormLabelMapping[HP_MODULE_REG]}
                options={HP_MODULE_OPTIONS}
                tooltip={TT_HP_MODULE}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MODULE_REF_REG}
                label={hpDeviceFormLabelMapping[HP_MODULE_REF_REG]}
                placeholder="Enter device module reference..."
                tooltip={TT_HP_MODULE_REF}
            />
            <Divider orientation="horizontal" />
            <Box>
                <Typography variant="h6">
                    Additional Components Associated with this Device
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Use the below section to outline any additional components
                    or back-up modules associated with this device. If the
                    device does not have any additional components, please
                    answer no the the following questions
                </Typography>
            </Box>
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_BACK_UP_HEATER_PRESENT_REG}
                label={hpDeviceFormLabelMapping[HP_BACK_UP_HEATER_PRESENT_REG]}
                radioOptions={IS_MODULE_PRESENT_OPTIONS}
                tooltip={TT_HP_BACK_UP_HEATER_PRESENT}
            />

            <DisplayIf
                condition={
                    watch(HP_BACK_UP_HEATER_PRESENT_REG) === IS_MODULE_PRESENT
                }
            >
                <>
                    <ValidatedRadioGroup
                        control={control}
                        required
                        registerName={HP_BACK_UP_HEATER_PHASES_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BACK_UP_HEATER_PHASES_REG
                            ]
                        }
                        radioOptions={LCT_PHASES_OPTIONS}
                        tooltip={TT_HP_BACK_UP_HEATER_PHASES}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={HP_BACK_UP_HEATER_MODULE_REF_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BACK_UP_HEATER_MODULE_REF_REG
                            ]
                        }
                        placeholder="Enter module reference for back up heater..."
                        tooltip={TT_HP_BACK_UP_HEATER_MODULE_REF}
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={HP_BACK_UP_HEATER_MODULE_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BACK_UP_HEATER_MODULE_REG
                            ]
                        }
                        options={HP_BACK_UP_HEATER_MODULE_OPTIONS}
                        tooltip={TT_HP_BACK_UP_HEATER_MODULE}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                            ]
                        }
                        placeholder="Enter total input rated current..."
                        tooltip={
                            TT_HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT
                        }
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated current must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER_REG
                            ]
                        }
                        tooltip={TT_HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER}
                        placeholder="Enter total input rated power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated power must be a valid positive number",
                            },
                        }}
                    />
                </>
            </DisplayIf>
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_BOOST_HEATER_PRESENT_REG}
                label={hpDeviceFormLabelMapping[HP_BOOST_HEATER_PRESENT_REG]}
                radioOptions={IS_MODULE_PRESENT_OPTIONS}
                tooltip={TT_HP_BOOST_HEATER_PRESENT}
            />
            <DisplayIf
                condition={
                    watch(HP_BOOST_HEATER_PRESENT_REG) === IS_MODULE_PRESENT
                }
            >
                <>
                    <ValidatedRadioGroup
                        control={control}
                        required
                        registerName={HP_BOOST_HEATER_PHASES_REG}
                        label={
                            hpDeviceFormLabelMapping[HP_BOOST_HEATER_PHASES_REG]
                        }
                        radioOptions={LCT_PHASES_OPTIONS}
                        tooltip={TT_HP_BOOST_HEATER_PHASES}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={HP_BOOST_HEATER_MODULE_REF_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BOOST_HEATER_MODULE_REF_REG
                            ]
                        }
                        placeholder="Enter module reference for boost heater..."
                        tooltip={TT_HP_BOOST_HEATER_MODULE_REF}
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={HP_BOOST_HEATER_MODULE_REG}
                        label={
                            hpDeviceFormLabelMapping[HP_BOOST_HEATER_MODULE_REG]
                        }
                        options={HP_BOOST_HEATER_MODULE_OPTIONS}
                        tooltip={TT_HP_BOOST_HEATER_MODULE}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                            ]
                        }
                        tooltip={TT_HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT}
                        placeholder="Enter total input rated current..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated current must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER_REG
                            ]
                        }
                        tooltip={TT_HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER}
                        placeholder="Enter total input rated power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated power must be a valid positive number",
                            },
                        }}
                    />
                </>
            </DisplayIf>
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_WATER_HEATER_PRESENT_REG}
                label={hpDeviceFormLabelMapping[HP_WATER_HEATER_PRESENT_REG]}
                radioOptions={IS_MODULE_PRESENT_OPTIONS}
                tooltip={TT_HP_WATER_HEATER_PRESENT}
            />
            <DisplayIf
                condition={
                    watch(HP_WATER_HEATER_PRESENT_REG) === IS_MODULE_PRESENT
                }
            >
                <>
                    <ValidatedRadioGroup
                        control={control}
                        required
                        registerName={HP_WATER_HEATER_PHASES_REG}
                        label={
                            hpDeviceFormLabelMapping[HP_WATER_HEATER_PHASES_REG]
                        }
                        radioOptions={LCT_PHASES_OPTIONS}
                        tooltip={TT_HP_WATER_HEATER_PHASES}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={HP_WATER_HEATER_MODULE_REF_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_WATER_HEATER_MODULE_REF_REG
                            ]
                        }
                        placeholder="Enter module reference for water heater..."
                        tooltip={TT_HP_WATER_HEATER_MODULE_REF}
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={HP_WATER_HEATER_MODULE_REG}
                        label={
                            hpDeviceFormLabelMapping[HP_WATER_HEATER_MODULE_REG]
                        }
                        options={HP_WATER_HEATER_MODULE_OPTIONS}
                        tooltip={TT_HP_WATER_HEATER_MODULE}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                            ]
                        }
                        placeholder="Enter total input rated current..."
                        tooltip={TT_HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT}
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated current must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_WATER_HEATER_MODULE_INPUT_RATED_POWER_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_WATER_HEATER_MODULE_INPUT_RATED_POWER_REG
                            ]
                        }
                        tooltip={TT_HP_WATER_HEATER_MODULE_INPUT_RATED_POWER}
                        placeholder="Enter total input rated power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated power must be a valid positive number",
                            },
                        }}
                    />
                </>
            </DisplayIf>
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_HEAT_PUMP_PRESENT_REG}
                label={hpDeviceFormLabelMapping[HP_HEAT_PUMP_PRESENT_REG]}
                radioOptions={IS_MODULE_PRESENT_OPTIONS}
                tooltip={TT_HP_HEAT_PUMP_PRESENT}
            />
            <DisplayIf
                condition={
                    watch(HP_HEAT_PUMP_PRESENT_REG) === IS_MODULE_PRESENT
                }
            >
                <>
                    <ValidatedRadioGroup
                        control={control}
                        required
                        registerName={HP_HEAT_PUMP_PHASES_REG}
                        label={
                            hpDeviceFormLabelMapping[HP_HEAT_PUMP_PHASES_REG]
                        }
                        tooltip={TT_HP_HEAT_PUMP_PHASES}
                        radioOptions={LCT_PHASES_OPTIONS}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={HP_HEAT_PUMP_MODULE_REF_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_HEAT_PUMP_MODULE_REF_REG
                            ]
                        }
                        tooltip={TT_HP_HEAT_PUMP_MODULE_REF}
                        placeholder="Enter module reference for the back up heat pump..."
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={HP_HEAT_PUMP_MODULE_REG}
                        label={
                            hpDeviceFormLabelMapping[HP_HEAT_PUMP_MODULE_REG]
                        }
                        tooltip={TT_HP_HEAT_PUMP_MODULE}
                        options={HP_HEAT_PUMP_MODULE_OPTIONS}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT_REG
                        }
                        label={
                            hpDeviceFormLabelMapping[
                                HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT_REG
                            ]
                        }
                        tooltip={TT_HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT}
                        placeholder="Enter total input rated current..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated current must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER_REG}
                        label={
                            hpDeviceFormLabelMapping[
                                HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER_REG
                            ]
                        }
                        tooltip={TT_HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER}
                        placeholder="Enter total input rated power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated power must be a valid positive number",
                            },
                        }}
                    />
                </>
            </DisplayIf>
            <Divider orientation="horizontal" />
            <Box>
                <Typography variant="h6">
                    Additional Optional Details
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Nearly there! These final fields are optional. Please enter
                    if known
                </Typography>
            </Box>
            <ValidatedTextField
                control={control}
                required={false}
                registerName={HP_NOM_CURRENT_W35_REG}
                label={hpDeviceFormLabelMapping[HP_NOM_CURRENT_W35_REG]}
                placeholder="Enter device nominal current A-7 / W35..."
                tooltip={TT_HP_NOM_CURRENT_W35}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device nominal current A-7 / W35 must be a valid positive number",
                    },
                }}
                optional
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={HP_NOM_CURRENT_W45_REG}
                label={hpDeviceFormLabelMapping[HP_NOM_CURRENT_W45_REG]}
                placeholder="Enter device nominal current A-7 / W45..."
                tooltip={TT_HP_NOM_CURRENT_W45}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device nominal current A-7 / W45 must be a valid positive number",
                    },
                }}
                optional
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={HP_NOM_CURRENT_W55_REG}
                label={hpDeviceFormLabelMapping[HP_NOM_CURRENT_W55_REG]}
                placeholder="Enter device nominal current A-7 / W55..."
                tooltip={TT_HP_NOM_CURRENT_W55}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device nominal current A-7 / W55 must be a valid positive number",
                    },
                }}
                optional
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={HP_NOM_CURRENT_W65_REG}
                label={hpDeviceFormLabelMapping[HP_NOM_CURRENT_W65_REG]}
                placeholder="Enter device nominal current A-7 / W65..."
                tooltip={TT_HP_NOM_CURRENT_W65}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device nominal current A-7 / W65 must be a valid positive number",
                    },
                }}
                optional
            />
            <FormErrors
                errors={errors}
                getFriendlyName={(key: string) => hpDeviceFormLabelMapping[key]}
            />
            <FormNavigation
                onClickBack={onClickBack}
                onClickNext={() => handleSubmit(onClickNext)()}
                nextText="Next"
                onClickMiddle={onSaveDraft}
                middleDisabled={saveDraftLoading}
            />
        </>
    )
}

const CoreHpDeviceDetails = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const { formIsLoading, currentIndex } = useContext(context)

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Core Heat Pump Device Details"
                    subHeader="To start, please enter the following core details about the device"
                    index={currentIndex + 1}
                />
                {formIsLoading ? (
                    <CoreDeviceDetailsSkeleton />
                ) : (
                    <CoreHpDeviceDetailsForm context={context} />
                )}
            </Stack>
        </AppCard>
    )
}

export default CoreHpDeviceDetails
